import type { CasinoSecondaryNavigation } from '@swf/cms';
import type { UseWidget } from '@swf/core/hooks/useWidget';
import { useWidget } from '@swf/core/hooks/useWidget';
import type {
  ExtractDispatchFromModel,
  Slicer,
  WidgetEffectParams,
  WidgetModelConfig
} from '@swf/core/store';
import { select } from '@swf/core/store';
import type { CasinoGame } from '@swf/core/types/CasinoGame';
import { login } from '@swf/core/utils/actions';
import { marketingTagsWithSite } from '@swf/core/utils/casino';

export interface CasinoSecondaryNavigationState {
  recent?: CasinoGame[];
  recommended?: CasinoGame[];
}

const reducers = {
  set: (state: CasinoSecondaryNavigationState, payload: CasinoSecondaryNavigationState) => ({
    ...state,
    ...payload
  })
};

export const userFilters = {
  recent: 'casino.games.recent.stream',
  recommended: 'casino.games.recommended.stream'
};

export const userMatcher = new RegExp('(recent|recommended)');

export const queryParams = {
  pg: 0,
  psz: 24,
  ims: { tags: ['icnSquare', 'icnWide'] },
  qry: [{ formalparamcode: 'tagname', values: ['Slots'], operand: 1, join: 1, dynamic: false }],
  grp: true,
  tgs: true,
  includeTags: marketingTagsWithSite,
  order: [
    { column: 'tagName::Top [BRAND] [SITE]', sort: 1 },
    { column: 'numOf', sort: 1 }
  ]
};

const effects = {
  async preload(payload: undefined, params: WidgetEffectParams<CasinoSecondaryNavigation>) {
    const { widget, dispatch, actions, rootState } = params;
    const widgetDispatch = dispatch[widget.uid];
    const clientAuthenticated = select.session.isClientAuthenticated(rootState);
    if (clientAuthenticated) {
      widgetDispatch.fetch();
    }
    login(actions)?.subscribe(async () => {
      widgetDispatch.fetch();
    });
  },
  async fetch(payload: undefined, params: WidgetEffectParams<CasinoSecondaryNavigation>) {
    const { dispatch, request, widget } = params;
    const body = queryParams;
    const widgetDispatch: ExtractDispatchFromModel<CasinoSecondaryNavigationModel> =
      dispatch[widget.uid];

    Object.keys(userFilters).forEach((key) => {
      request({ body, service: userFilters[key] }).then((data) => {
        widgetDispatch.set({ [key]: data?.body?.games || [] });
      });
    });
  }
};

const selectors = (slice: Slicer<CasinoSecondaryNavigationState>) => ({
  get() {
    return slice;
  }
});

export interface CasinoSecondaryNavigationModel
  extends WidgetModelConfig<CasinoSecondaryNavigation, CasinoSecondaryNavigationState> {
  reducers: typeof reducers;
  effects: typeof effects;
  selectors: typeof selectors;
}

export const secondaryNavigationWidgetModel: CasinoSecondaryNavigationModel = {
  state: { recent: [], recommended: [] },
  reducers,
  effects,
  selectors
};

export const useSecondaryNavWidgetModel: UseWidget<
  CasinoSecondaryNavigation,
  CasinoSecondaryNavigationModel
> = useWidget;
